import { Action, createReducer, createFeatureSelector, createSelector, on } from '@ngrx/store';
import { Cart } from '@shared/models/cart';
import { LoadingState } from '@shared/types/loading-state';
import { loadCart, loadCartFailure, loadCartSuccess } from '../actions/load-cart.actions';
import { createCart, createCartFailure, createCartSuccess } from '../actions/create-cart.actions';
import { addCartItem, addCartItemFailure, addCartItemSuccess } from '../actions/add-cart-item.actions';
import { removeCartItem, removeCartItemFailure, removeCartItemSuccess } from '../actions/remove-cart-item.actions';
import {
  updateCartItemQuantity,
  updateCartItemQuantityFailure,
  updateCartItemQuantitySuccess,
} from '../actions/update-cart-item-quantity.actions';
import {
  updateCartItemOffering,
  updateCartItemOfferingFailure,
  updateCartItemOfferingSuccess,
} from '../actions/update-cart-item-offering.actions';
import {
  addCartItemDiscount,
  addCartItemDiscountFailure,
  addCartItemDiscountSuccess,
} from '../actions/add-cart-item-discount.actions';
import {
  removeCartItemDiscount,
  removeCartItemDiscountFailure,
  removeCartItemDiscountSuccess,
} from '../actions/remove-cart-item-discount.actions';
import { createQuotes, createQuotesFailure, createQuotesSuccess } from '../actions/create-quotes.actions';
import { createOrder, createOrderFailure } from '../actions/create-order.actions';
import { setCartConfig } from '../actions/set-cart-config.actions';
import { selectCartCustomer, selectCartCustomerValidations, selectCartItemId } from './cart-config.reducer';
import { Period, PricingPeriod } from '@shared/types/period';
import { selectAllOfferings, selectOfferedOfferings } from '@features/offerings/shared/reducers/offerings.reducer';
import { loadQuoteFailure, loadQuoteSuccess } from '@features/quotes/shared/actions/load-quote.actions';
import {
  updateCustomer,
  updateCustomerFailure,
  updateCustomerSuccess,
} from '@features/customers/admin/actions/update-customer.actions';
import { setCustomExpirationDate } from '../actions/set-custom-expiration-date.actions';
import { updateSelectedPeriods } from '../actions/update-selected-periods.actions';
import {
  updateCartItemCustomExpirationDate,
  updateCartItemCustomExpirationDateFailure,
  updateCartItemCustomExpirationDateSuccess,
} from '../actions/update-cart-item-custom-expiration-date.actions';

export const featureKey = 'cart';

export interface State {
  loadingState: LoadingState;
  loadingText: string | undefined;
  cart: Cart | undefined;
  selectedPeriods: PricingPeriod[];
  customExpirationDate: string | undefined;
  preferredPeriod: Period | undefined;
}

export const initialState: State = {
  loadingState: 'not_loading',
  loadingText: undefined,
  cart: undefined,
  selectedPeriods: [],
  customExpirationDate: undefined,
  preferredPeriod: undefined,
};

export const cartReducer = createReducer(
  initialState,
  on(loadCart, createCart, (state, {}) => ({
    ...state,
    loadingText: undefined,
    loadingState: 'loading' as LoadingState,
  })),
  on(addCartItem, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: 'Adding Subscription...',
    selectedPeriods: [],
  })),
  on(removeCartItem, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: 'Removing Subscription...',
  })),
  on(updateCartItemQuantity, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: 'Updating Quantity...',
  })),
  on(updateCartItemOffering, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: 'Changing Subscription...',
    selectedPeriods: [],
  })),
  on(updateCartItemCustomExpirationDate, (state, { custom_expiration_date }) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: !custom_expiration_date ? 'Removing Custom Period...' : 'Updating Custom Period...',
  })),
  on(addCartItemDiscount, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: 'Applying Promotion...',
  })),
  on(removeCartItemDiscount, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: 'Removing Promotion...',
  })),
  on(updateCustomer, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: 'Updating Customer...',
  })),
  on(createQuotes, (state, { quote_type, periods, opportunity_id }) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText:
      quote_type == 'pricing'
        ? `Creating Estimate${periods.length > 1 ? 's' : ''}...`
        : opportunity_id
          ? `Creating Quote${periods.length > 1 ? 's' : ''}...`
          : `Creating Opportunity...`,
  })),
  on(createOrder, (state, { quote_type }) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText:
      quote_type == 'nfr'
        ? `${state.cart?.cart_items[0]?.support_code ? 'Modifying ' : 'Creating'} NFR...`
        : quote_type == 'trial'
          ? 'Creating Trial...'
          : 'Placing Order...',
  })),
  on(
    loadCartSuccess,
    createCartSuccess,
    addCartItemSuccess,
    removeCartItemSuccess,
    updateCartItemQuantitySuccess,
    updateCartItemOfferingSuccess,
    addCartItemDiscountSuccess,
    removeCartItemDiscountSuccess,
    (state, { cart }) => ({
      ...state,
      loadingState: 'loaded' as LoadingState,
      loadingText: undefined,
      cart: cart,
      selectedPeriods: ['enlarge', 'upgrade', 'trial', 'nfr'].includes(cart?.quote_type!)
        ? [cart?.cart_items[0].period as PricingPeriod]
        : state.selectedPeriods,
      customExpirationDate: cart?.cart_items[0]?.custom_expiration_date,
    }),
  ),
  on(updateCartItemCustomExpirationDateSuccess, (state, { cart }) => ({
    ...state,
    loadingState: 'loaded' as LoadingState,
    loadingText: undefined,
    cart: cart,
    customExpirationDate: cart?.cart_items[0]?.custom_expiration_date,
    selectedPeriods: [],
    preferredPeriod: undefined,
  })),
  on(updateCustomerSuccess, (state) => ({
    ...state,
    loadingState: 'loaded' as LoadingState,
    loadingText: undefined,
  })),
  on(loadQuoteSuccess, createQuotesSuccess, (state, {}) => ({
    ...state,
    loadingState: 'not_loading' as LoadingState,
    loadingText: undefined,
    cart: undefined,
    customExpirationDate: undefined,
    selectedPeriods: [],
    preferredPeriod: undefined,
  })),
  on(setCartConfig, (state, { cartConfig, clearCart }) => ({
    ...state,
    cart: clearCart ? undefined : state.cart,
    loadingText: clearCart ? undefined : state.loadingText,
    customExpirationDate: clearCart ? undefined : state.customExpirationDate,
    selectedPeriods: clearCart ? [] : state.selectedPeriods,
    preferredPeriod: clearCart ? undefined : state.preferredPeriod,
  })),
  on(
    loadCartFailure,
    createCartFailure,
    addCartItemFailure,
    removeCartItemFailure,
    updateCartItemQuantityFailure,
    updateCartItemOfferingFailure,
    updateCartItemCustomExpirationDateFailure,
    addCartItemDiscountFailure,
    removeCartItemDiscountFailure,
    createQuotesFailure,
    createOrderFailure,
    loadQuoteFailure,
    updateCustomerFailure,
    (state, {}) => ({
      ...state,
      loadingState: 'not_loading' as LoadingState,
      loadingText: undefined,
    }),
  ),
  on(setCustomExpirationDate, (state, { customExpirationDate }) => ({
    ...state,
    customExpirationDate: customExpirationDate,
  })),
  on(updateSelectedPeriods, (state, { period }) => ({
    ...state,
    selectedPeriods: state.selectedPeriods.includes(period)
      ? state.selectedPeriods.filter((selectedPeriod) => selectedPeriod !== period)
      : [...state.selectedPeriods, period],
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return cartReducer(state, action);
}

export const cartFeatureSelector = createFeatureSelector<State>(featureKey);

export const selectCartLoadingState = createSelector(cartFeatureSelector, (state) => state.loadingState);

export const selectCartLoadingText = createSelector(cartFeatureSelector, (state) => state.loadingText);

export const selectCart = createSelector(cartFeatureSelector, (state) => state.cart);

export const selectCustomExpirationDate = createSelector(cartFeatureSelector, (state) => state.customExpirationDate);

export const selectSelectedPeriods = createSelector(cartFeatureSelector, (state) => state.selectedPeriods);

export const cartValidationSeatThreshold = 100;

export const selectSelectedCartItem = createSelector(selectCart, selectCartItemId, (cart, cartItemId) =>
  cart?.cart_items.find((cartItem) => cartItem.id === cartItemId),
);

export const selectCartTotal = (pricingPeriod: PricingPeriod) =>
  createSelector(selectCart, (cart) => cart?.totals[pricingPeriod]);

export const selectAllCartItemsWithDiscountsForPeriod = (pricingPeriod: PricingPeriod) =>
  createSelector(selectCart, (cart) =>
    cart?.cart_items.filter((cartItem) => !!cartItem.pricing[pricingPeriod]?.discount),
  );
export const selectAllCartItemsOfferingCodes = createSelector(selectCart, (cart) =>
  cart?.cart_items.map((cartItem) => cartItem.offering_code),
);

export const selectCartTotalCartItemOfferingsWithPricing = (pricingPeriod: PricingPeriod) =>
  createSelector(selectCart, selectAllOfferings, (cart, offerings) =>
    !cart || !offerings
      ? []
      : cart.cart_items.map((cartItem) => ({
          cartItem,
          cartItemPricing: cartItem.pricing[pricingPeriod]!,
          offering: offerings.find((offering) => offering.attributes.code === cartItem.offering_code)!,
        })),
  );

export const selectApprovalNeededForPeriods = createSelector(selectCart, (cart) => {
  let pricingPeriods: PricingPeriod[] = ['year_1', 'year_2', 'year_3', 'custom'];
  return !cart
    ? []
    : pricingPeriods.filter(
        (pricingPeriod) =>
          cart.cart_items.filter((cartItem) => !!cartItem.pricing[pricingPeriod]?.approval_type).length > 0,
      );
});

export const selectSkipExternalCreationAvailableForPeriods = createSelector(
  selectApprovalNeededForPeriods,
  selectCart,
  (approvalNeededPeriods, cart) => {
    return !cart || !approvalNeededPeriods
      ? false
      : approvalNeededPeriods.filter(
          (pricingPeriod) =>
            cart.cart_items.filter(
              (cartItem) =>
                !!cartItem.pricing[pricingPeriod]?.approval_type &&
                cartItem.pricing[pricingPeriod]?.discount?.attributes?.discount_type == 'custom',
            ).length > 0,
        ).length > 0;
  },
);

export const selectHasEsetServicesInCart = createSelector(selectCart, (cart) => {
  return !cart ? false : cart.cart_items.filter((cartItem) => cartItem.offering_type == 'shared_services').length > 0;
});

export const selectHasServicesValidationErrors = createSelector(
  selectHasEsetServicesInCart,
  selectCartCustomerValidations,
  selectCart,
  (hasEsetServicesInCart, cartCustomerValidations, cart) => {
    return (
      hasEsetServicesInCart &&
      Object.values(cartCustomerValidations || false).includes(true) &&
      cart?.quote_type !== 'pricing'
    );
  },
);

export const selectFirstCartItemOffering = createSelector(selectCart, selectOfferedOfferings, (cart, offerings) =>
  offerings.find((offering) => cart?.cart_items[0]?.offering_code == offering.attributes.code),
);

export const selectCartLargeQuantityValidations = createSelector(
  selectCart,
  selectCartCustomerValidations,
  selectCartCustomer,
  (cart, cartCustomerValidations, cartCustomer) => {
    return !cart
      ? false
      : Object.values(cartCustomerValidations || false).includes(true) &&
          cart.cart_items.reduce((acc, cartItem) => acc + cartItem.quantity, 0) >= cartValidationSeatThreshold;
  },
);

export const selectCorrectCustomerEmailAddressRequiredForCart = createSelector(
  selectCart,
  selectCartCustomerValidations,
  (cart, cartCustomerValidations) => {
    return !cart
      ? false
      : cart.cart_items.filter((cart_item) => ['147', '148', '149'].includes(cart_item.offering_code)).length > 0;
  },
);

export const selectValidationForCart = createSelector(
  selectCart,
  selectCartCustomerValidations,
  selectCartLargeQuantityValidations,
  selectHasServicesValidationErrors,
  (cart, cartCustomerValidations, largeQuantityValidation, hasServicesValidationErrors) => {
    return {
      ...cartCustomerValidations,
      largeQuantityWithInvalidEntity: largeQuantityValidation,
      hasServicesError: hasServicesValidationErrors,
    };
  },
);
